import '@justgiving/loading/src/Loading.scss';
import '@justgiving/styles';
import '@justgiving/page-components';

import { FunctionComponent, Suspense, lazy, useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';

import { AuthProvider } from '../../hooks/useAuthenticatedUser';
import { CustomOptimizelyProvider } from '../../Optimizely/CustomOptimizelyProvider';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { GlobalStyle } from '../../styling/GlobalStyle';
import { I18nextProvider } from 'react-i18next';
import { LoadingPage } from '../../pages/Loading/LoadingPage';
import { PageComponentsProvider } from '@justgiving/page-components';
import { Toaster } from 'react-hot-toast';
import { WindowBackdrop } from '../PopUpWindow/WindowBackdrop';
import config from '../../config';
import { goToUrl } from '../../utils/goToUrl';
import i18n from '../../i18next';
import { getLoginUrl } from '../../utils/auth';
import { pageCreatedUrlVar } from '../../state/appState';
import { pageCreationTheme } from '../../styling/theme';
import { pages } from '../../constants/pages';
import { useReactiveVar } from '@apollo/client';
import { withProfiler } from '@sentry/react';

const StoryEnhancerDemoRouter = lazy(
  () =>
    import(
      /* webpackChunkName: "StoryEnhancerDemoRouter" */ '../../components/Routers/StoryEnhancerDemoRouter'
    )
);
const CampaignRouter = lazy(
  () =>
    import(
      /* webpackChunkName: "CampaignRouter" */ '../../components/Routers/CampaignRouter'
    )
);
const ChooseFundraisingTypeRoute = lazy(
  () =>
    import(
      /* webpackChunkName: "ChooseFundraisingTypeRoute" */ '../Routers/ChooseFundraisingTypeRoute'
    )
);
const FundraisingRouter = lazy(
  () =>
    import(
      /* webpackChunkName: "FundraisingRouter" */ '../Routers/FundraisingRouter'
    )
);
const TeamRouter = lazy(
  () => import(/* webpackChunkName: "TeamRouter" */ '../Routers/TeamRouter')
);
const CrowdfundingRouter = lazy(
  () =>
    import(
      /* webpackChunkName: "CrowdfundingRouter" */ '../Routers/CrowdfundingRouter'
    )
);

const ClaimRouter = lazy(
  () => import(/* webpackChunkName: "ClaimRouter" */ '../Routers/ClaimRouter')
);

const App: FunctionComponent<React.PropsWithChildren<unknown>> = () => {
  const pageCreatedUrl = useReactiveVar(pageCreatedUrlVar);

  useEffect(() => {
    if (pageCreatedUrl) {
      goToUrl(pageCreatedUrl);
    }
  }, [pageCreatedUrl]);

  return (
    <AuthProvider>
      <CustomOptimizelyProvider>
        <ThemeProvider theme={pageCreationTheme}>
          <GlobalStyle />
          <I18nextProvider i18n={i18n}>
            <StyledPageComponentsProvider>
              <Toaster position="bottom-center" />
              <ErrorBoundary loginUrl={getLoginUrl()}>
                <Suspense fallback={<LoadingPage />}>
                  <Switch>
                    <Route
                      path={`/${pages.campaign}`}
                      component={CampaignRouter}
                    />
                    <Route path={`/${pages.team}`} component={TeamRouter} />
                    <Route
                      path={`/${pages.storyEnhancerDemo}`}
                      component={StoryEnhancerDemoRouter}
                    />
                    <Route
                      path={`/${pages.crowdfunding}`}
                      component={CrowdfundingRouter}
                    />
                    {config.featureFlags.enableClaimPageCreation && (
                      <Route path={`/${pages.claim}`} component={ClaimRouter} />
                    )}
                    <Route
                      path="/"
                      exact
                      component={ChooseFundraisingTypeRoute}
                    />
                    <Route path="/" component={FundraisingRouter} />
                  </Switch>
                </Suspense>
              </ErrorBoundary>
            </StyledPageComponentsProvider>
          </I18nextProvider>
        </ThemeProvider>
      </CustomOptimizelyProvider>
      <WindowBackdrop />
    </AuthProvider>
  );
};

export default withProfiler(App);

const StyledPageComponentsProvider = styled(PageComponentsProvider)`
  display: contents;
`;
